import useSWR, { mutate } from 'swr';

import usePvsPractitioners from '@/hooks/usePvsPractitioners.ts';
import useTasks from '@/hooks/useTasks.ts';

import { EnrichedTask } from '@/models/doc-cirrus/DCTask.ts';

import { getTask } from '@/services/tasksService.ts';

import { enrichTask } from '@/utils/task-utils.ts';

interface TaskHook {
    task: EnrichedTask | undefined;
    is_loading: boolean;
}

interface UseTaskConfig {
    task_id?: string;
    instance_id?: string;
}

const useTask = ({ task_id, instance_id }: UseTaskConfig): TaskHook => {
    const { instance_users_map } = usePvsPractitioners();
    const { task_types } = useTasks();

    const getTaskAndEnrich = async (instance_id?: string, task_id?: string): Promise<EnrichedTask | undefined> => {
        if (!instance_id || !task_id) return undefined;

        const task = await getTask(instance_id, task_id);
        if (!task || !task_types) return undefined;

        return enrichTask(task, task_types, instance_users_map);
    };

    const { data: task, isLoading: is_loading } = useSWR(
        // Only fetch when task_types are actually loaded (not empty) to avoid stale closure
        instance_id && task_id && task_types.length > 0
            ? { key: 'task', instance_id: instance_id, task_id: task_id }
            : undefined,
        () => getTaskAndEnrich(instance_id, task_id)
    );

    return {
        task,
        is_loading,
    };
};

export const mutateTask = async (task_id?: string, instance_id?: string): Promise<void> => {
    if (!task_id || !instance_id) return;

    await mutate({ key: 'task', instance_id: instance_id, task_id: task_id });
};

export default useTask;
